import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/warehouse/warehouseAdd.module.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createWarehouse, getCountries, getStates, getCities } from '../../services/Api';
import PopUpMessage from '../../utils/errors/PopUpMessage';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import { setShouldRefresh } from '../../features/warehouse/warehouseSlice';
import { AiOutlineLoading } from 'react-icons/ai';

const WarehouseAdd = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState({
        countries: false,
        states: false,
        cities: false
    });

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        country_id: '',
        country_code: '',
        state_id: '',
        city_id: '',
        status: ''
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const validationRules = {
        name: {
            required: true,
            message: 'Please enter a warehouse name'
        },
        address: {
            required: true,
            message: 'Please enter a warehouse address'
        },
        country_id: {
            required: true,
            message: 'Please select a warehouse country'
        },
        state_id: {
            required: true,
            message: 'Please select a warehouse state',
            validate: (value) => {
                // Allow empty or "no-states" option (which will be handled differently)
                return value !== '';
            }
        },
        city_id: {
            required: true,
            message: 'Please select a warehouse city',
            validate: (value) => {
                // Allow empty or "no-cities" option (which will be handled differently)
                return value !== '';
            }
        },
        status: {
            required: true,
            message: 'Please select a warehouse status'
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'country_id') {
            // When country changes, we need to find both the ID and code
            const selectedCountry = countries.find(country =>
                country.id == value ||
                country.country_code === value ||
                country.code === value
            );

            setFormData(prev => {
                const newData = {
                    ...prev,
                    country_id: selectedCountry?.id || '',      // Store numeric ID
                    country_code: selectedCountry?.code || selectedCountry?.country_code || '', // Store code
                    state_id: '',
                    city_id: ''
                };
                return newData;
            });

            // Reset dependent dropdowns
            setStates([]);
            setCities([]);

        } else {
            setFormData(prev => {
                const newData = {
                    ...prev,
                    [name]: value
                };

                // Reset dependent dropdowns when parent changes
                if (name === 'state_id') {
                    newData.city_id = '';
                    setCities([]);

                    if (value === 'no-states') {
                        setCities([{
                            id: 'no-cities',
                            name: 'No cities available',
                            disabled: true
                        }]);
                    }
                }

                return newData;
            });
        }

        // Mark field as touched when changed
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Style select elements based on value
        const selectElement = e.target;
        if (value && value !== 'no-states' && value !== 'no-cities') {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        // Clear errors for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        if (value.trim() !== '') {
            validateField(name, value);
        }
    };

    const validateField = (name, value) => {
        const rule = validationRules[name];
        if (!rule) return true;

        if (rule.required && !value) {
            setErrors(prev => ({
                ...prev,
                [name]: rule.message
            }));
            return false;
        }

        if (rule.pattern && value && !rule.pattern.test(value)) {
            setErrors(prev => ({
                ...prev,
                [name]: rule.message
            }));
            return false;
        }

        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
        return true;
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Mark all fields as touched when submitting
        const allTouched = {};
        Object.keys(validationRules).forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);

        Object.keys(validationRules).forEach(field => {
            const rule = validationRules[field];
            const value = formData[field];

            // Skip validation for state_id if it's "no-states" option
            if (field === 'state_id' && value === 'no-states') {
                return;
            }

            // Skip validation for city_id if it's "no-cities" option
            if (field === 'city_id' && value === 'no-cities') {
                return;
            }

            // Check required fields
            if (rule.required && !value) {
                newErrors[field] = rule.message;
                isValid = false;
                return;
            }

            // Check custom validation if exists
            if (rule.validate && !rule.validate(value)) {
                newErrors[field] = rule.message;
                isValid = false;
                return;
            }

            // Check pattern matching (if specified)
            if (rule.pattern && value && !rule.pattern.test(value)) {
                newErrors[field] = rule.message;
                isValid = false;
                return;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        // First validate the form
        if (!validateForm()) {
            showPopup('error', 'Validation Error', 'Please fill all required fields correctly');
            return;
        }

        if (formData.state_id === 'no-states' || formData.city_id === 'no-cities') {
            showPopup('error', 'Validation Error', 'Selected location does not have states or cities available. Please select a different country.');
            return;
        }

        setLoading(true);

        try {
            const apiData = {
                name: formData.name,
                address: formData.address,
                country_id: formData.country_id,      // Send numeric ID
                state_id: formData.state_id,
                city_id: formData.city_id,
                status: formData.status === '1' ? '1' : '0',
            };

            console.log('Sending data to API:', apiData); // Debug log

            const response = await createWarehouse(apiData);

            if (response.status === 200) {
                showPopup('success', 'Success', 'Warehouse created successfully');
                dispatch(setShouldRefresh(true));
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/warehouse");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to create warehouse';
            showPopup('error', 'Error', errorMessage);

            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                // Mark fields with errors as touched
                const newTouched = {};
                Object.keys(error.response.data.errors).forEach(field => {
                    newTouched[field] = true;
                });
                setTouched(prev => ({ ...prev, ...newTouched }));
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper function to determine if a field should show error styling
    const shouldShowError = (fieldName) => {
        return touched[fieldName] && errors[fieldName];
    };

    // Fetch countries on component mount
    useEffect(() => {
        const fetchCountries = async () => {
            setLoadingLocations(prev => ({ ...prev, countries: true }));
            try {
                const response = await getCountries();
                console.log('Countries API Response:', response); // Debug log

                // Handle different possible response structures
                let countriesData = [];
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    countriesData = response.data.data;
                } else if (response?.data && Array.isArray(response.data)) {
                    countriesData = response.data;
                } else if (Array.isArray(response)) {
                    countriesData = response;
                }

                if (countriesData.length > 0) {
                    setCountries(countriesData);
                } else {
                    console.error("No countries data found in response:", response);
                    showPopup('error', 'Error', 'No countries data available');
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
                showPopup('error', 'Error', 'Failed to load countries');
            } finally {
                setLoadingLocations(prev => ({ ...prev, countries: false }));
            }
        };

        fetchCountries();
    }, []);

    // Fetch states when country changes
    useEffect(() => {
        const fetchStates = async () => {
            if (!formData.country_code) {
                setStates([]);
                return;
            }

            setLoadingLocations(prev => ({ ...prev, states: true }));
            try {
                // Use country_code to fetch states
                const response = await getStates(formData.country_code);
                console.log('States API Response:', response); // Debug log

                // Handle different possible response structures
                let statesData = [];
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    statesData = response.data.data;
                } else if (response?.data && Array.isArray(response.data)) {
                    statesData = response.data;
                } else if (Array.isArray(response)) {
                    statesData = response;
                }

                if (statesData.length === 0) {
                    setStates([{
                        id: 'no-states',
                        name: 'No states available',
                        disabled: true
                    }]);
                    setFormData(prev => ({ ...prev, state_id: '' }));
                } else {
                    setStates(statesData);
                }
            } catch (error) {
                console.error("Error fetching states:", error);
                showPopup('error', 'Error', 'Failed to load states');
                setStates([{
                    id: 'no-states',
                    name: 'No states available',
                    disabled: true
                }]);
                setFormData(prev => ({ ...prev, state_id: '' }));
            } finally {
                setLoadingLocations(prev => ({ ...prev, states: false }));
            }
        };

        fetchStates();
    }, [formData.country_code]);

    // Fetch cities when state changes
    useEffect(() => {
        const fetchCities = async () => {
            if (!formData.state_id) {
                setCities([]);
                return;
            }

            setLoadingLocations(prev => ({ ...prev, cities: true }));
            try {
                const response = await getCities(formData.state_id);
                console.log('Cities API Response:', response); // Debug log

                // Handle different possible response structures
                let citiesData = [];
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    citiesData = response.data.data;
                } else if (response?.data && Array.isArray(response.data)) {
                    citiesData = response.data;
                } else if (Array.isArray(response)) {
                    citiesData = response;
                }

                if (citiesData.length === 0) {
                    setCities([{
                        id: 'no-cities',
                        name: 'No cities available',
                        disabled: true
                    }]);
                    setFormData(prev => ({ ...prev, city_id: '' }));
                } else {
                    setCities(citiesData);
                }
            } catch (error) {
                console.error("Error fetching cities:", error);
                showPopup('error', 'Error', 'Failed to load cities');
                setCities([{
                    id: 'no-cities',
                    name: 'No cities available',
                    disabled: true
                }]);
                setFormData(prev => ({ ...prev, city_id: '' }));
            } finally {
                setLoadingLocations(prev => ({ ...prev, cities: false }));
            }
        };

        fetchCities();
    }, [formData.state_id]);

    return (
        <div className={`${Styles.warehouse_add}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Create Warehouse</h2>
            </div>

            <div className={`${Styles.warehouse_form}`}>
                <div className={`${Styles.warehouse_details}`}>
                    <div className={`${Styles.title}`}>
                        <h4>Warehouse details</h4>
                        <hr />
                    </div>
                    <div className={`${Styles.form}`}>
                        <div className={`${Styles.label_input} ${shouldShowError('name') ? Styles.error : ''}`}>
                            <label>Warehouse Name<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    placeholder="Enter Warehouse Name"
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={shouldShowError('name') ? Styles.errorInput : ''}
                                />
                                {shouldShowError('name') &&
                                    <span className={`${Styles.errorMessage}`}>{errors.name}</span>
                                }
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${shouldShowError('address') ? Styles.error : ''}`}>
                            <label>Warehouse Address<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    placeholder="Enter Address"
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={shouldShowError('address') ? Styles.errorInput : ''}
                                />
                                {shouldShowError('address') &&
                                    <span className={`${Styles.errorMessage}`}>{errors.address}</span>
                                }
                            </div>
                        </div>

                        <div className={`${Styles.label_input}`}>
                            <label>Country / State / City<span>*</span></label>
                            <div className={`${Styles.select_container}`}>
                                <div className={`${Styles.country}`}>
                                    <select
                                        name="country_id"
                                        value={formData.country_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        className={shouldShowError('country_id') ? Styles.errorInput : ''}
                                        disabled={loadingLocations.countries}
                                    >
                                        <option value="" hidden>
                                            {loadingLocations.countries ? 'Loading countries...' : 'Select Country'}
                                        </option>
                                        {countries.map((country) => (
                                            <option key={country.id} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                    {shouldShowError('country_id') &&
                                        <span className={`${Styles.errorMessage}`}>{errors.country_id}</span>
                                    }
                                </div>

                                <div className={`${Styles.state}`}>
                                    <select
                                        name="state_id"
                                        value={formData.state_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        disabled={!formData.country_id || loadingLocations.states}
                                        className={shouldShowError('state_id') ? Styles.errorInput : ''}
                                    >
                                        <option value="" hidden>
                                            {loadingLocations.states
                                                ? 'Loading states...'
                                                : 'Select State'
                                            }
                                        </option>
                                        {states.map((state) => (
                                            <option key={state.id} value={state.id} disabled={state.disabled || false}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </select>
                                    {shouldShowError('state_id') &&
                                        <span className={`${Styles.errorMessage}`}>{errors.state_id}</span>
                                    }
                                </div>

                                <div className={`${Styles.city}`}>
                                    <select
                                        name="city_id"
                                        value={formData.city_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        disabled={!formData.state_id || loadingLocations.cities || formData.state_id === 'no-states'}
                                        className={shouldShowError('city_id') ? Styles.errorInput : ''}
                                    >
                                        <option value="" hidden>
                                            {loadingLocations.cities
                                                ? 'Loading cities...'
                                                : formData.state_id === 'no-states'
                                                    ? 'No cities available'
                                                    : 'Select City'
                                            }
                                        </option>
                                        {cities.map((city) => (
                                            <option key={city.id} value={city.id} disabled={city.disabled || false}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                    {shouldShowError('city_id') &&
                                        <span className={`${Styles.errorMessage}`}>{errors.city_id}</span>
                                    }
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.label_input} ${shouldShowError('status') ? Styles.error : ''}`}>
                            <label>Status<span>*</span></label>
                            <div className={`${Styles.select_field}`}>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    className={shouldShowError('status') ? Styles.errorInput : ''}
                                >
                                    <option value="" hidden>Select Status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                                {shouldShowError('status') &&
                                    <span className={`${Styles.errorMessage}`}>{errors.status}</span>
                                }
                            </div>
                        </div>
                    </div>

                    <div className={`${Styles.buttons}`}>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? <AiOutlineLoading /> : "Create"}
                        </button>
                        <button onClick={() => navigate("/warehouse")}>Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WarehouseAdd;
